import { Paperclip, X } from 'lucide-react';
import { HelpTip } from '../../../../components/HelpTip';
import { Tooltip } from '../../../../components/Tooltip';
import { TdSeamless } from '../../../../components/inline-add-table/cells';
import { formatBytes } from '../../../../lib/formatBytes';

/** A stable empty default — a fresh `[]` per render would be a new prop every time. */
const NONE_CARRIED: readonly { fileName: string; sizeBytes: number }[] = [];

/**
 * The same picker as a GRID CELL — the receiving event's per-item files.
 *
 * A native file input kept in the tab run (`sr-only`, never `display:none`, so a keyboard reader
 * reaches it and `focusValidationError` can land on it), with its label drawn as the cell. What
 * was chosen is summarised in the cell and listed in full in the shared `<Tooltip>` — a grid row
 * keeps one height, so the list cannot hang under the control the way the field's does.
 *
 * `disabledReason` is for STATE — a shelved kind has no evidence file — and the cell says why.
 */
export function EvidenceFilesCell({
  id,
  ariaLabel,
  multiple = false,
  max,
  accept,
  files,
  onChange,
  disabled = false,
  disabledReason = null,
  invalid = false,
  carried = NONE_CARRIED,
}: {
  id: string;
  ariaLabel: string;
  multiple?: boolean;
  max?: number;
  accept: string[];
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  disabledReason?: string | null;
  invalid?: boolean;
  /**
   * Documents already filed — a REQUEST line's, carried onto the item when it is received. Listed and
   * counted against `max`, never removable here: they are part of what the requester handed over.
   */
  carried?: readonly { fileName: string; sizeBytes: number }[];
}) {
  const inert = disabled || disabledReason !== null;
  const count = carried.length + files.length;
  const full = max !== undefined && count >= max;
  const firstName = carried[0]?.fileName ?? files[0]?.name;

  const summary =
    count === 0
      ? multiple
        ? 'Choose files'
        : 'Choose a file'
      : count === 1
        ? firstName
        : `${count} files`;

  const listed = [
    ...carried.map((c) => `${c.fileName} · ${formatBytes(c.sizeBytes)} (from the request)`),
    ...files.map((f) => `${f.name} · ${formatBytes(f.size)}`),
  ].join(', ');

  if (disabledReason !== null) {
    return (
      <TdSeamless>
        <Tooltip disabledReason={disabledReason} block>
          <span className="text-sm text-slate-400">—</span>
        </Tooltip>
      </TdSeamless>
    );
  }

  return (
    <TdSeamless>
      <input
        id={id}
        name={id}
        type="file"
        multiple={multiple}
        accept={accept.join(',')}
        disabled={inert || full}
        aria-label={ariaLabel}
        aria-invalid={invalid ? true : undefined}
        onChange={(event) => {
          const picked = Array.from(event.target.files ?? []);
          const next = multiple ? [...files, ...picked] : picked.slice(0, 1);

          // The cap counts the carried documents too.
          onChange(max === undefined ? next : next.slice(0, Math.max(0, max - carried.length)));

          // Cleared so the same file can be picked again after removing it.
          event.target.value = '';
        }}
        className="peer sr-only"
      />

      <Tooltip label={count > 0 ? listed : full ? `At most ${max}.` : ariaLabel} block>
        <label
          htmlFor={id}
          className={`flex min-w-0 items-center gap-1.5 rounded px-1 py-0.5 text-sm peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500/40 ${
            invalid ? 'text-red-700' : count === 0 ? 'text-slate-400' : 'text-slate-600'
          }`}
        >
          <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="min-w-0 truncate">{summary}</span>
        </label>
      </Tooltip>

      {files.length > 0 ? (
        <button
          type="button"
          aria-label={`Remove the files in ${ariaLabel}`}
          disabled={inert}
          onClick={() => onChange([])}
          className="ml-auto shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ) : null}
    </TdSeamless>
  );
}

/**
 * A file picker for the evidence forms — one file, or several up to a cap.
 *
 * <b>The shape of the Files module's own picker</b> (`<UploadFileModal>`): a native file input
 * styled the same way, the accepted types beside it, what was chosen listed under it. There is no
 * shared file input to reach for; six forms each draw one, which is a gap in `components/` rather
 * than something to solve here.
 *
 * The `accept` list is a CONVENIENCE for the browser's dialog, never a check: the server types a
 * file by its extension against the configured evidence types, then verifies its magic bytes.
 */
export function EvidenceFilesField({
  id,
  label,
  description,
  required = false,
  multiple = false,
  max,
  accept,
  files,
  onChange,
  disabled = false,
  error,
}: {
  id: string;
  label: string;
  description: string;
  required?: boolean;
  multiple?: boolean;
  /** The most files the field takes. Extra picks are dropped, and the field says so. */
  max?: number;
  accept: string[];
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  error?: string;
}) {
  const describedBy = `${id}-description`;
  const full = max !== undefined && files.length >= max;

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5">
        <label htmlFor={id} className="text-sm font-medium text-slate-700">
          {label}
          {required ? <span className="ml-0.5 text-red-600" aria-hidden="true">*</span> : null}
        </label>
        <HelpTip label={description} ariaLabel={`About ${label}`} />
      </div>

      {/* The description's screen-reader twin — the help icon is out of the tab run. */}
      <span id={describedBy} className="sr-only">
        {description}
      </span>

      <input
        id={id}
        name={id}
        type="file"
        multiple={multiple}
        accept={accept.join(',')}
        disabled={disabled || full}
        aria-describedby={describedBy}
        aria-required={required}
        aria-invalid={error ? true : undefined}
        onChange={(event) => {
          const picked = Array.from(event.target.files ?? []);
          const next = multiple ? [...files, ...picked] : picked.slice(0, 1);

          onChange(max === undefined ? next : next.slice(0, max));

          // Cleared so the same file can be picked again after removing it.
          event.target.value = '';
        }}
        className={`block w-full rounded-lg border bg-white text-sm text-slate-600 file:mr-3 file:cursor-pointer file:rounded-l-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:text-slate-700 hover:file:bg-slate-200 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400 ${
          error ? 'border-red-400' : 'border-slate-300'
        }`}
      />

      {files.length > 0 ? (
        /*
          FILE CHIPS in a wrapping row below the picker — product call, 09 Oct 2026 (captured from the image):
          a paperclip, the name and an x, each in a small bordered chip. No size, as the image shows none.
        */
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${index}`}
              className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600"
            >
              <Paperclip className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
              <span className="min-w-0 truncate">{file.name}</span>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                disabled={disabled}
                onClick={() => onChange(files.filter((_, i) => i !== index))}
                className="shrink-0 rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {full ? <p className="mt-1.5 text-xs text-slate-500">That is the most one entry can carry ({max}).</p> : null}

      {error ? (
        <p role="alert" className="mt-1.5 text-xs text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
